/**
 * ProfileArchetypesSection — Displays archetype badges on the profile view page.
 *
 * Shows up to 3 archetype icons with labels. Visibility-gated:
 * - Owner always sees it (with private/public badge)
 * - Non-owner only sees it when archetypesVisibility === 'public'
 *
 * When the owner has no archetypes selected, shows an empty-state card
 * prompting them to visit settings.
 *
 * Icons are lazy-loaded via React.lazy() to keep the main bundle small.
 *
 * @route /profile/:uid
 */

import React, {
  Suspense, useEffect, useMemo, useRef, useState,
} from 'react'
import ProfileIdentityCardHeader from '../../shared/ProfileIdentityCardHeader/index'
import { useTranslatedText } from '../../../../StaticText/StaticTextContext'
import { ARCHETYPES } from '../../../../../services/community/identity/profile/constants'
import getArchetypeIcon from '../../../../../services/community/identity/profile/archetype-icons'

/** Figma max badge size at desktop (3107-118418). */
const MAX_BADGE_SIZE = 100
/** Floor so badges never vanish on very narrow cards. */
const MIN_BADGE_SIZE = 48
/** Matches the CSS gap between badges in `_ProfileArchetypesSection.scss`. */
const GRID_GAP_PX = 4

const BLOCK = 'profile-archetypes'

export interface ProfileArchetypesSectionProps {
  archetypes: string[]
  /**
   * Profile field visibility. May be null/undefined for non-owner viewers —
   * the backend hides *Visibility settings from non-owners. A null value
   * means "no restriction known to this viewer"; only an explicit 'private'
   * hides the section from non-owners. The parent is the source of truth
   * via isFieldVisible.
   */
  archetypesVisibility?: string | null
  isOwner: boolean
  onSettingsClick?: () => void
}

function ProfileArchetypesSection ({
  archetypes,
  archetypesVisibility,
  isOwner,
  onSettingsClick,
}: ProfileArchetypesSectionProps) {
  const title = useTranslatedText({ keys: ['archetypesTitle'] })
  const emptyText = useTranslatedText({ keys: ['archetypesEmptyText'] })
  const emptyLinkText = useTranslatedText({ keys: ['archetypesEmptyLink'] })
  const emptySuffix = useTranslatedText({ keys: ['archetypesEmptySuffix'] })
  // The label for each archetype is translated; the badge sub-component
  // resolves it via useTranslatedText, so a variable-length archetypes array
  // doesn't violate the rules of hooks. Filter out unknown keys here.
  const archetypeEntries = useMemo(() => archetypes
    .filter((key) => ARCHETYPES.some((a) => a.key === key))
    .map((key) => ({
      key,
      Icon: getArchetypeIcon(key),
    })), [archetypes])

  // The <Icon> wrapper (lib/components/Icon/Icon.jsx) writes width/height
  // as an inline style on its span — CSS can't override without !important
  // (banned). To make the archetype SVGs actually scale with the card,
  // observe the grid container width and compute a badge size that fits
  // all badges on one row, capped at the Figma max (100px).
  const gridRef = useRef<HTMLDivElement>(null)
  const [gridWidth, setGridWidth] = useState(0)

  useEffect(() => {
    const el = gridRef.current
    if (!el || typeof ResizeObserver === 'undefined') return undefined
    const ro = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setGridWidth(entry.contentRect.width)
      })
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const archetypeIconSize = useMemo(() => {
    const count = archetypeEntries.length
    if (!count) return MAX_BADGE_SIZE
    if (gridWidth <= 0) return MAX_BADGE_SIZE // SSR / pre-measure
    const totalGap = GRID_GAP_PX * (count - 1)
    const perBadge = Math.floor((gridWidth - totalGap) / count)
    return Math.max(MIN_BADGE_SIZE, Math.min(MAX_BADGE_SIZE, perBadge))
  }, [archetypeEntries.length, gridWidth])

  const isPrivate = archetypesVisibility === 'private'

  if (!isOwner && isPrivate) return null

  if (archetypes.length === 0) {
    if (!isOwner) return null
    return (
      <div className={BLOCK}>
        <ProfileIdentityCardHeader
          className={BLOCK}
          title={title}
          isOwner={isOwner}
          isPopulated={false}
          description={emptyText}
          settingsLinkText={emptyLinkText}
          settingsLinkSuffix={emptySuffix}
          onSettingsClick={onSettingsClick}
        />
      </div>
    )
  }

  return (
    <div className={BLOCK}>
      <ProfileIdentityCardHeader
        className={BLOCK}
        title={title}
        isOwner={isOwner}
        isPopulated
      />
      <div ref={gridRef} className={`${BLOCK}__grid`}>
        {archetypeEntries.map(({ key, Icon }) => (
          <ArchetypeBadge
            key={key}
            archetypeKey={key}
            Icon={Icon}
            size={archetypeIconSize}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Single archetype badge. Extracted so `useTranslatedText` resolves the
 * archetype's label without calling hooks in a `.map(...)` over a
 * variable-length array (Rules of Hooks). Lookup key derives from the
 * stable archetype identifier (`archetype_<key>`) under the
 * `communityProfilePage` slice.
 */
interface ArchetypeBadgeProps {
  archetypeKey: string
  Icon: ReturnType<typeof getArchetypeIcon> | undefined
  size: number
}

function ArchetypeBadge ({ archetypeKey, Icon, size }: ArchetypeBadgeProps) {
  const label = useTranslatedText({ keys: [`archetype_${archetypeKey}`] })
  return (
    <div className={`${BLOCK}__item`}>
      {Icon ? (
        <Suspense
          fallback={(
            <div
              className={`${BLOCK}__icon-placeholder`}
              style={{ width: size, height: size }}
            />
          )}
        >
          <Icon size={size} />
        </Suspense>
      ) : (
        <div
          className={`${BLOCK}__icon-placeholder`}
          style={{ width: size, height: size }}
        />
      )}
      <span className={`${BLOCK}__label`}>{label}</span>
    </div>
  )
}

export default React.memo(ProfileArchetypesSection)
